/**
 * Pure helpers for Compás board offline (moodboard / resumen / ficha_tecnica).
 * No IndexedDB here — keep unit-testable.
 */

const OFFLINE_KINDS = new Set(["moodboard", "resumen", "ficha_tecnica"]);

export function normalizeOfflineBoardKind(kind) {
  const k = String(kind || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  if (!k || k === "default") return "moodboard";
  if (k === "resumenes" || k === "summary" || k === "recap" || k === "recaps") {
    return "resumen";
  }
  if (
    k === "fichatecnica" ||
    k === "tech_pack" ||
    k === "techpack" ||
    k === "ficha"
  ) {
    return "ficha_tecnica";
  }
  if (
    k === "aidesign" ||
    k === "ai_design" ||
    k === "sketch" ||
    k === "sketches" ||
    k === "silueta" ||
    k === "siluetas" ||
    k === "nuevo_diseno" ||
    k === "nuevo_disenos"
  ) {
    return "ai_design";
  }
  return k;
}

/** True when this Kind can use the shared PagesJson offline editor. */
export function isOfflineEligibleBoardKind(kind) {
  return OFFLINE_KINDS.has(normalizeOfflineBoardKind(kind));
}

export function isBrowserOnline(
  nav = typeof navigator !== "undefined" ? navigator : null,
) {
  if (!nav || typeof nav.onLine !== "boolean") return true;
  return nav.onLine;
}

/**
 * Decide whether a pages/meta save should go to the local queue
 * instead of hitting the API.
 */
export function shouldQueueOfflineSave({
  online,
  offlineEnabled,
  networkError = false,
} = {}) {
  if (!offlineEnabled) return false;
  if (!online) return true;
  return Boolean(networkError);
}

export function buildPendingPagesPayload({
  pages,
  pasteboard,
  expectedUpdatedAt,
  savedAt = null,
} = {}) {
  return {
    pages: Array.isArray(pages) ? pages : [],
    pasteboard: pasteboard === undefined ? undefined : pasteboard,
    expectedUpdatedAt: expectedUpdatedAt || null,
    savedAt: savedAt || new Date().toISOString(),
  };
}

export function buildPendingMetaPayload({
  titulo,
  categoryLabel,
  studioLine,
  chromeStyle,
  expectedUpdatedAt,
  savedAt = null,
} = {}) {
  return {
    Titulo: titulo,
    CategoryLabel: categoryLabel,
    StudioLine: studioLine,
    ChromeStyleJson: chromeStyle,
    expectedUpdatedAt: expectedUpdatedAt || null,
    savedAt: savedAt || new Date().toISOString(),
  };
}

/** Apply queued local edits onto a cached GET snapshot for display. */
export function mergePendingIntoSnapshot(snapshot, pendingPages, pendingMeta) {
  if (!snapshot || typeof snapshot !== "object") return snapshot;
  const next = { ...snapshot };
  if (pendingPages) {
    if (Array.isArray(pendingPages.pages)) next.PagesJson = pendingPages.pages;
    if (pendingPages.pasteboard !== undefined) {
      next.Pasteboard = pendingPages.pasteboard;
    }
  }
  if (pendingMeta) {
    if (pendingMeta.Titulo !== undefined) next.Titulo = pendingMeta.Titulo;
    if (pendingMeta.CategoryLabel !== undefined) {
      next.CategoryLabel = pendingMeta.CategoryLabel;
    }
    if (pendingMeta.StudioLine !== undefined) {
      next.StudioLine = pendingMeta.StudioLine;
    }
    if (pendingMeta.ChromeStyleJson !== undefined) {
      next.ChromeStyleJson = pendingMeta.ChromeStyleJson;
    }
  }
  return next;
}
